import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { fetchCompanyFactoryOverview } from '../services/api';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useTranslation } from '../context/LanguageContext';
import './CollectionStatusIndicator.css';

// 가동/대기/정지 판정은 백엔드(EquipmentLiveStatus)가 내려준 값을 그대로 쓴다.
// 마지막 샷 기준 10분 이내 가동 / 30분 이내 대기 / 초과 정지.
// 프론트에서 따로 계산하면 서버·브라우저 시계 차이로 카운트와 목록이 어긋날 수 있어
// factory.equipment(집계)와 machine.status(설비별)를 같은 출처로 사용한다.
const EMPTY_COUNTS = { total: 0, running: 0, idle: 0, stopped: 0 };

const minutesSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 60000);
};

// 정지 = 30분 이상 데이터 미수신 (백엔드 판정)
const isStopped = (machine) => machine.status === 'stopped';

// 공장 단위 판정
//  - none      : 등록된 설비 없음
//  - collecting: 정지 설비 없음
//  - partial   : 일부 설비만 정지
//  - stopped   : 전 설비 정지 (수집기 자체 장애 가능성)
const getFactoryCollectionStatus = (counts) => {
  if (counts.total === 0) return 'none';
  if (counts.stopped === 0) return 'collecting';
  return counts.stopped === counts.total ? 'stopped' : 'partial';
};

function CollectionStatusIndicator() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  const anchorRef = useRef(null);
  const portalRef = useRef(null);
  const [popupPos, setPopupPos] = useState({ top: 0, right: 0 });

  // 팝업 위치 계산
  const updatePopupPos = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPopupPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  };

  // 팝업 열림 시 위치 계산 + 리사이즈/스크롤 갱신
  useEffect(() => {
    if (!showPopup) return;
    updatePopupPos();
    window.addEventListener('resize', updatePopupPos);
    window.addEventListener('scroll', updatePopupPos, true);
    return () => {
      window.removeEventListener('resize', updatePopupPos);
      window.removeEventListener('scroll', updatePopupPos, true);
    };
  }, [showPopup]);

  // 팝업 외부 클릭 시 닫기
  useEffect(() => {
    if (!showPopup) return;
    const handleClickOutside = (e) => {
      const inAnchor = anchorRef.current && anchorRef.current.contains(e.target);
      const inPortal = portalRef.current && portalRef.current.contains(e.target);
      if (!inAnchor && !inPortal) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup]);

  // 데이터 로드
  const loadCollectionStatus = async () => {
    try {
      const data = await fetchCompanyFactoryOverview();
      setFactories(data?.factories || []);
    } catch (err) {
      console.error('Failed to load collection status:', err);
      setFactories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollectionStatus();
  }, []);

  useAutoRefresh(() => {
    loadCollectionStatus();
  });

  // 공장별 카운트 + 정지 설비 목록 (오래 멈춘 설비가 위로)
  const factoryRows = factories.map((factory) => {
    const counts = factory.equipment || EMPTY_COUNTS;
    const stoppedMachines = (factory.machines || [])
      .filter(isStopped)
      .map((m) => ({ ...m, staleMinutes: minutesSince(m.lastShotAt) }))
      .sort((a, b) => (b.staleMinutes ?? Infinity) - (a.staleMinutes ?? Infinity));
    return {
      factory,
      counts,
      status: getFactoryCollectionStatus(counts),
      stoppedMachines,
    };
  });

  // 전 공장 합계 (헤더 배지에 가동중/대기/정지를 점+숫자로 표시)
  const totals = factoryRows.reduce(
    (acc, r) => ({
      running: acc.running + (r.counts.running || 0),
      idle: acc.idle + (r.counts.idle || 0),
      stopped: acc.stopped + (r.counts.stopped || 0),
    }),
    { running: 0, idle: 0, stopped: 0 }
  );

  const stoppedTotal = totals.stopped;
  const hasIssue = stoppedTotal > 0;

  // 경과 시간 표기 (분 → 분/시간/일)
  const formatElapsed = (mins) => {
    if (mins === null || mins === undefined) return t('header.collection.neverCollected');
    if (mins < 60) return t('header.collection.elapsedMin', { n: mins });
    if (mins < 60 * 24) return t('header.collection.elapsedHour', { n: Math.floor(mins / 60) });
    return t('header.collection.elapsedDay', { n: Math.floor(mins / (60 * 24)) });
  };

  const handleFactoryClick = (factory) => {
    setShowPopup(false);
    navigate(`/factory?factoryId=${factory.factoryId}`);
  };

  const handleMachineClick = (e, machine) => {
    e.stopPropagation();
    setShowPopup(false);
    // Machine 화면은 쿼리 파라미터가 아니라 location.state 의 machineData 로 대상 설비를 받는다.
    // (다른 화면들과 동일한 방식. 쿼리로 넘기면 무시되고 첫 번째 설비가 열린다)
    navigate('/machine', {
      state: { machineData: { id: machine.equipmentId, name: machine.equipmentName } },
    });
  };

  return (
    <div className="collection-status-container">
      <div
        ref={anchorRef}
        className={`collection-status-box ${loading ? 'loading' : hasIssue ? 'has-issue' : 'normal'}`}
        onClick={() => setShowPopup((prev) => !prev)}
        title={t('header.collection.title')}
      >
        <span className="material-symbols-outlined collection-status-icon">
          {loading ? 'sensors' : hasIssue ? 'sensors_off' : 'sensors'}
        </span>
        {loading ? (
          <span className="collection-status-text">{t('header.collection.loading')}</span>
        ) : (
          <span className="collection-status-summary">
            <span className="cs-dot-count" title={t('header.collection.countRunning')}>
              <i className="cs-dot cs-dot-running" />
              {totals.running}
            </span>
            <span className="cs-dot-count" title={t('header.collection.countIdle')}>
              <i className="cs-dot cs-dot-idle" />
              {totals.idle}
            </span>
            <span className="cs-dot-count" title={t('header.collection.countStopped')}>
              <i className="cs-dot cs-dot-stopped" />
              {totals.stopped}
            </span>
          </span>
        )}
      </div>

      {showPopup &&
        ReactDOM.createPortal(
          <div
            ref={portalRef}
            className="collection-status-dropdown collection-status-dropdown-portal"
            style={{
              top: `${popupPos.top}px`,
              right: `${popupPos.right}px`,
            }}
          >
            <div className="collection-status-dropdown-header">
              <h3>{t('header.collection.title')}</h3>
              <div className="collection-status-header-meta">
                <span className="collection-status-count">
                  {t('header.collection.factoryCount', { count: factories.length })}
                </span>
                {hasIssue && (
                  <span className="collection-status-stopped-badge">
                    {t('header.collection.stoppedBadge', { count: stoppedTotal })}
                  </span>
                )}
              </div>
            </div>

            {!hasIssue && (
              <div className="collection-status-all-normal">
                <span className="material-symbols-outlined">check_circle</span>
                <span>{t('header.collection.allNormal')}</span>
              </div>
            )}

            <div className="collection-status-list">
              {factoryRows.length === 0 ? (
                <div className="collection-status-empty">{t('header.collection.empty')}</div>
              ) : (
                factoryRows.map(({ factory, status, counts, stoppedMachines }) => (
                  <div
                    key={factory.factoryId}
                    className={`collection-status-item status-${status}`}
                    onClick={() => handleFactoryClick(factory)}
                  >
                    <div className="collection-status-item-main">
                      <span className={`collection-status-dot dot-${status}`}></span>
                      <span className="collection-status-factory-name">{factory.factoryName}</span>

                      {/* 상태 배지 자리에 가동중(초록)·대기(노랑)·정지(빨강) 대수를 표시.
                          '정지 N대' 배지는 아래 정지 설비 목록과 중복이라 뺐다. */}
                      {counts.total === 0 ? (
                        <span className="collection-status-badge badge-none">
                          {t('header.collection.statusNoEquipment')}
                        </span>
                      ) : (
                        <span className="collection-status-counts">
                          <span className="ccount ccount-running">
                            {t('header.collection.countRunning')}
                            <b>{counts.running}</b>
                          </span>
                          <span className="ccount ccount-idle">
                            {t('header.collection.countIdle')}
                            <b>{counts.idle}</b>
                          </span>
                          <span className="ccount ccount-stopped">
                            {t('header.collection.countStopped')}
                            <b>{counts.stopped}</b>
                          </span>
                        </span>
                      )}
                    </div>

                    {stoppedMachines.length > 0 && (
                      <ul className="collection-status-stale-list">
                        {stoppedMachines.map((m) => (
                          <li
                            key={m.equipmentId}
                            className="collection-status-stale-item"
                            onClick={(e) => handleMachineClick(e, m)}
                          >
                            <span className="collection-status-stale-name">{m.equipmentName}</span>
                            <span className="collection-status-stale-elapsed">{formatElapsed(m.staleMinutes)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default CollectionStatusIndicator;
